/* global SideDrawer */

class WishlistManager {
  constructor() {
    this.storageKey = 'deepa_wishlist';
  }

  getWishlist() {
    try {
      const list = localStorage.getItem(this.storageKey);
      return list ? JSON.parse(list) : [];
    } catch (e) {
      console.error('Error reading wishlist from localStorage', e);
      return [];
    }
  }

  saveWishlist(list) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(list));
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist: list } }));
    } catch (e) {
      console.error('Error saving wishlist to localStorage', e);
    }
  }

  isInWishlist(handle) {
    const list = this.getWishlist();
    return list.some(item => item.handle === handle);
  }

  toggleWishlist(item) {
    const list = this.getWishlist();
    const index = list.findIndex(existing => existing.handle === item.handle);

    if (index > -1) {
      list.splice(index, 1);
    } else {
      list.push(item);
    }
    this.saveWishlist(list);
  }

  removeFromWishlist(handle) {
    const list = this.getWishlist();
    const updated = list.filter(item => item.handle !== handle);
    this.saveWishlist(updated);
  }
}

window.wishlistManager = new WishlistManager();

class WishlistDrawer extends SideDrawer {
  connectedCallback() {
    this.bindEvents();
    this.render();
  }

  bindEvents() {
    this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    
    document.addEventListener('dispatch:wishlist-drawer:open', this.openDrawerViaEventHandler);
    document.addEventListener('dispatch:wishlist-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('wishlist:updated', () => {
      this.render();
      this.updateBadges();
    });

    // Handle clicks inside the drawer
    this.addEventListener('click', (e) => {
      // Remove button
      const removeBtn = e.target.closest('.js-wishlist-remove');
      if (removeBtn) {
        const handle = removeBtn.dataset.handle;
        window.wishlistManager.removeFromWishlist(handle);
        return;
      }

      // Add to cart button
      const atcBtn = e.target.closest('.js-wishlist-atc');
      if (atcBtn) {
        this.handleAddToCart(atcBtn);
      }
    });

    // Initial badge update
    this.updateBadges();
    this.syncToggles();
  }

  disconnectedCallback() {
    document.removeEventListener('dispatch:wishlist-drawer:open', this.openDrawerViaEventHandler);
    document.removeEventListener('dispatch:wishlist-drawer:close', this.closeDrawerViaEventHandler);
  }

  handleDrawerOpenViaEvent(evt) {
    this.open(evt.detail ? evt.detail.opener : null);
  }

  updateBadges() {
    const count = window.wishlistManager.getWishlist().length;
    document.querySelectorAll('.js-wishlist-count').forEach(badge => {
      badge.textContent = count;
      badge.setAttribute('data-count', count);
      if (count === 0) {
        badge.style.display = 'none';
      } else {
        badge.style.display = 'flex';
      }
    });
    this.syncToggles();
  }

  syncToggles() {
    const wishlist = window.wishlistManager.getWishlist();
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const handle = btn.dataset.wishlistToggle;
      const isActive = wishlist.some(item => item.handle === handle);
      
      if (isActive) {
        btn.classList.add('is-active');
        const textSpan = btn.querySelector('.wishlist-btn__text');
        if (textSpan) {
          textSpan.textContent = 'Remove from Wishlist';
        }
      } else {
        btn.classList.remove('is-active');
        const textSpan = btn.querySelector('.wishlist-btn__text');
        if (textSpan) {
          textSpan.textContent = 'Add to Wishlist';
        }
      }
    });
  }

  async handleAddToCart(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      // Close wishlist drawer
      this.close();

      // Dispatch events to refresh the cart drawer
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));

      // Open the cart drawer
      const cartDrawer = document.querySelector('.js-cart-drawer');
      if (cartDrawer) {
        // Wait a small moment for cart refresh to initiate or finish
        setTimeout(() => {
          cartDrawer.open(btn);
        }, 100);
      }
    } catch (error) {
      console.error('Error adding item to cart', error);
      btn.textContent = 'Error';
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 2000);
    }
  }

  render() {
    const listContainer = this.querySelector('.js-wishlist-items');
    if (!listContainer) return;

    const wishlist = window.wishlistManager.getWishlist();
    listContainer.textContent = '';

    if (wishlist.length === 0) {
      listContainer.innerHTML = `
        <div class="wishlist-drawer__empty">
          <span class="wishlist-drawer__empty-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
            </svg>
          </span>
          <p class="wishlist-drawer__empty-text">Your wishlist is currently empty.</p>
          <a href="/collections/all" class="btn btn--secondary wishlist-drawer__empty-btn js-close-drawer">Browse Products</a>
        </div>
      `;
      return;
    }

    wishlist.forEach(item => {
      listContainer.appendChild(this.createWishlistItem(item));
    });
  }

  createWishlistItem(item) {
    const itemEl = document.createElement('div');
    itemEl.className = 'wishlist-item';
    itemEl.dataset.handle = item.handle || '';

    const imageLink = document.createElement('a');
    imageLink.className = 'wishlist-item__image-link';
    imageLink.href = this.safeUrl(item.url);

    const image = document.createElement('img');
    image.className = 'wishlist-item__image';
    image.src = this.safeUrl(item.image);
    image.alt = item.title || '';
    image.loading = 'lazy';
    image.width = 80;
    image.height = 100;
    imageLink.appendChild(image);

    const details = document.createElement('div');
    details.className = 'wishlist-item__details';

    if (item.vendor) {
      const vendor = document.createElement('span');
      vendor.className = 'wishlist-item__vendor';
      vendor.textContent = item.vendor;
      details.appendChild(vendor);
    }

    const title = document.createElement('a');
    title.className = 'wishlist-item__title heading-font';
    title.href = this.safeUrl(item.url);
    title.textContent = item.title || '';

    const price = document.createElement('span');
    price.className = 'wishlist-item__price';
    price.textContent = item.price || '';

    const actions = document.createElement('div');
    actions.className = 'wishlist-item__actions';

    const atc = document.createElement('button');
    atc.type = 'button';
    atc.className = 'wishlist-item__atc js-wishlist-atc';
    atc.dataset.variantId = item.variantId || '';
    atc.textContent = 'Add to Cart';

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'wishlist-item__remove js-wishlist-remove';
    remove.dataset.handle = item.handle || '';
    remove.setAttribute('aria-label', 'Remove item');
    remove.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>';

    actions.append(atc, remove);
    details.append(title, price, actions);
    itemEl.append(imageLink, details);
    return itemEl;
  }

  safeUrl(value) {
    try {
      const url = new URL(value || '', window.location.origin);
      return ['http:', 'https:'].includes(url.protocol) ? url.href : '#';
    } catch (e) {
      return '#';
    }
  }
}

window.customElements.define('wishlist-drawer', WishlistDrawer);

// Listen to all click events on the body to delegate wishlist toggle clicks
document.addEventListener('click', (e) => {
  const toggleBtn = e.target.closest('[data-wishlist-toggle]');
  if (!toggleBtn) return;

  e.preventDefault();
  const dataset = toggleBtn.dataset;
  const item = {
    handle: dataset.wishlistToggle,
    title: dataset.title,
    price: dataset.price,
    image: dataset.image,
    url: dataset.url,
    vendor: dataset.vendor || '',
    variantId: dataset.variantId
  };

  window.wishlistManager.toggleWishlist(item);
});

// Sync triggers on load
document.addEventListener('DOMContentLoaded', () => {
  if (window.wishlistManager) {
    const wishlist = window.wishlistManager.getWishlist();
    document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
      const handle = btn.dataset.wishlistToggle;
      const isActive = wishlist.some(item => item.handle === handle);
      if (isActive) {
        btn.classList.add('is-active');
        const textSpan = btn.querySelector('.wishlist-btn__text');
        if (textSpan) textSpan.textContent = 'Remove from Wishlist';
      }
    });
    
    // Trigger update badge counts on load
    document.querySelectorAll('.js-wishlist-count').forEach(badge => {
      const count = wishlist.length;
      badge.textContent = count;
      badge.setAttribute('data-count', count);
      if (count === 0) {
        badge.style.display = 'none';
      } else {
        badge.style.display = 'flex';
      }
    });
  }
});

// Trigger wishlist drawer open when clicking wishlist trigger in header
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('.js-wishlist-trigger');
  if (!trigger) return;

  e.preventDefault();
  const wishlistDrawer = document.querySelector('.js-wishlist-drawer');
  if (wishlistDrawer) {
    wishlistDrawer.open(trigger);
  }
});
